/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * ChangHeon Yoo (chyoo@rsupport.com)
 *
 * @description
 *
 * @created at Tue Oct 24 2023
 **/

import { useTheme } from "@emotion/react";
import { OAuthProvider } from "firebase/auth";
import { ChangeEvent, useEffect, useState } from "react";

import { _T, _P } from "@rfice/language";
import {
	Button,
	Checkbox,
	Column,
	Divider,
	Icon,
	Row,
	Switch,
	TextField,
	Typography,
	useModal,
	useToast
} from "@/frameworks/designStuff/components";

import Layout from "../../Layout";
import * as S from "../../emotion";
import { useUsers } from "@/frameworks/core/API";
import { ModalID, RegExp } from "@/frameworks/core/commons";
import { useAccountActions, useConfirm, useFirebaseAction } from "@/frameworks/core/hooks";

interface AccountSetting {
	move: React.Dispatch<React.SetStateAction<"delete" | "setting">>;
}

export default function AccountSetting({ move }: AccountSetting) {
	const [accountSetting, setAccountSetting] = useState<WebServices.UserOption.GetAccountOption.Data>();
	const [connectInfo, setConnectInfo] = useState<WebServices.User.GetConnectedUser.Data>();
	const { colors } = useTheme();
	const [connectType, setConnectType] = useState("");
	const { confirm } = useConfirm();
	const { logout } = useAccountActions();

	const modal = useModal();
	const toast = useToast();
	const userAPI = useUsers({
		onSuccess(name) {
			switch (name) {
				case "account:inquiry": {
					const { data } = userAPI.account.inquiry.result;
					setAccountSetting(data);
					break;
				}
				case "account:update": {
					break;
				}
				case "connect:inquiry": {
					const { data } = userAPI.connect.inquiry.result;
					setConnectInfo(data);
					break;
				}
				case "connect:update": {
					if (connectType === "email") {
						toast.show({ type: "system", message: _T("비밀번호가 변경되었습니다. 다시 로그인해주세요.") });
						logout();
					} else {
						toast.show({ type: "success", message: _T("연동 되었습니다.") });
					}
					modal.hideAll();
					break;
				}
				case "connect:delete": {
					toast.show({ type: "success", message: _T("연동 해제되었습니다.") });
					modal.hideAll();
					break;
				}
				case "update:marketing": {
					if (accountSetting?.marketingAgreement) {
						toast.show({
							type: "info",
							message: `${formatDate(new Date()) + _T("\n마케팅 정보 수신에 동의했습니다.")}`
						});
					} else {
						toast.show({
							type: "info",
							message: `${formatDate(new Date()) + _T("\n마케팅 정보 수신 동의가 철회되었습니다.")}`
						});
					}
				}
			}
		}
	});

	const handleToggle = (ev: ChangeEvent<HTMLInputElement>) => {
		const { id, checked } = ev.target;
		switch (id) {
			case "GOOGLE":
			case "APPLE": {
				if (!checked) {
					cancelConnect(id as "GOOGLE" | "APPLE");
				} else {
					handleLogin(id as "GOOGLE" | "APPLE");
				}
				break;
			}
			case "email": {
				if (!checked) {
					cancelConnect(id as "GOOGLE" | "APPLE" | "EMAIL");
				} else {
					modal.show({
						id: ModalID.RESET_PASSWORD,
						content: <ResetPassword />,
						preventHideByDimmed: true
					});
				}
				break;
			}
		}
	};
	const { handleLogin } = useFirebaseAction({
		// apple만 처리
		onSuccess(result, idp = "APPLE") {
			if (!result) return;
			const credential = OAuthProvider.credentialFromResult(result);
			const idToken = credential?.idToken;
			if (idToken) {
				handleSocialConnect(idp, idToken);
			}
		},
		onError(error) {
			console.debug("#AccountSetting# error", error);
		}
	});

	const cancelConnect = (connectType: "GOOGLE" | "EMAIL" | "APPLE") => {
		confirm(_T("연동을 해제하시겠습니까?"), {
			onConfirm() {
				userAPI.connect.delete.execute({ connectType: connectType });
			}
		});
	};
	const formatDate = (date: Date) => {
		const year = date.getFullYear();
		const month = String(date.getMonth() + 1).padStart(2, "0");
		const day = String(date.getDate()).padStart(2, "0");

		return `${year}.${month}.${day}`;
	};

	const handleCheckbox = (ev: React.ChangeEvent<HTMLInputElement>) => {
		const { id } = ev.target;

		switch (id) {
			case "anyoneByFriend": {
				setAccountSetting((prevSetting) => {
					if (prevSetting) {
						return {
							...prevSetting,
							allowFriendByGroupUser:
								!prevSetting[id as keyof WebServices.UserOption.GetAccountOption.Data],
							allowFriendBySpaceUser:
								!prevSetting[id as keyof WebServices.UserOption.GetAccountOption.Data]
						};
					} else {
						return prevSetting;
					}
				});
				break;
			}
			case "anyoneByFollow": {
				setAccountSetting((prevSetting) => {
					if (prevSetting) {
						return {
							...prevSetting,
							allowFollowByFriend: !prevSetting[id as keyof WebServices.UserOption.GetAccountOption.Data],
							allowFollowBySpaceUser:
								!prevSetting[id as keyof WebServices.UserOption.GetAccountOption.Data],
							allowFollowByGroupUser:
								!prevSetting[id as keyof WebServices.UserOption.GetAccountOption.Data]
						};
					} else {
						return prevSetting;
					}
				});
				break;
			}
			case "anyoneByDM": {
				setAccountSetting((prevSetting) => {
					if (prevSetting) {
						return {
							...prevSetting,
							allowDmByFriend: !prevSetting[id as keyof WebServices.UserOption.GetAccountOption.Data],
							allowDmByGroupUser: !prevSetting[id as keyof WebServices.UserOption.GetAccountOption.Data],
							allowDmBySpaceUser: !prevSetting[id as keyof WebServices.UserOption.GetAccountOption.Data]
						};
					} else {
						return prevSetting;
					}
				});
				break;
			}
			case "marketingAgreement": {
				setAccountSetting((prevSetting) => {
					if (prevSetting) {
						return {
							...prevSetting,
							marketingAgreement: !prevSetting[id as keyof WebServices.UserOption.GetAccountOption.Data]
						};
					} else {
						return prevSetting;
					}
				});
				if (accountSetting) {
					userAPI.marketingAgreement.execute({ marketingAgreement: !accountSetting[id] });
				}
				return;
			}
		}
		setAccountSetting((prevSetting) => {
			if (prevSetting) {
				return {
					...prevSetting,
					[id]: !prevSetting[id as keyof WebServices.UserOption.GetAccountOption.Data]
				};
			} else {
				return prevSetting;
			}
		});
	};

	const handleSave = () => {
		userAPI.account.update.execute(accountSetting);
	};
	const handleSocialConnect = (idp: "GOOGLE" | "APPLE", accessToken: string) => {
		userAPI.connect.update.execute({
			idp: idp,
			accessToken: accessToken,
			connectType: idp
		});
	};

	useEffect(() => {
		userAPI.account.inquiry.execute();
		userAPI.connect.inquiry.execute();
	}, []);

	const ResetPassword = () => {
		const [password, setPassword] = useState("");
		const [confirmPassword, setConfirmPassword] = useState("");
		const [passwordError, setPasswordError] = useState({
			hasError: false,
			message: ""
		});
		const [confirmError, setConfirmError] = useState({
			hasError: false,
			message: ""
		});
		const isValidPassword = () => {
			return RegExp.PASSWORD_REG.test(password);
		};

		const submitPassword = (ev: React.FormEvent<HTMLFormElement>) => {
			ev.preventDefault();
			if (!isValidPassword()) {
				setPasswordError({
					hasError: true,
					message: _T("8자 이상 24자 이하 영문, 숫자, 특수문자를 혼합하여 사용해 주세요.")
				});
				return;
			} else {
				setPasswordError({
					hasError: false,
					message: _T("password error")
				});
			}
			if (password !== confirmPassword) {
				setConfirmError({
					hasError: true,
					message: _T("비밀번호를 다시 확인해 주세요.")
				});
				return;
			} else {
				setConfirmError({
					hasError: false,
					message: _T("check password")
				});
				setConnectType("email");
				userAPI.connect.update.execute({
					connectType: "email",
					password
				});
			}
		};
		return (
			<Column.form
				style={{ padding: "32px 44px", width: "400px", textAlign: "start" }}
				gap={20}
				onSubmit={submitPassword}
			>
				<Typography typoSize="Head5">{_T("비밀번호 설정")}</Typography>
				<Typography typoSize="Body3">{_T("새로운 비밀번호를 입력해주세요.")}</Typography>
				<Column gap={12}>
					<input style={{ display: "none" }} autoComplete="new-password" />
					<TextField.Icon
						placeholder={_T("비밀번호")}
						type="password"
						autoComplete="new-password"
						onChange={(ev) => setPassword(ev.target.value)}
						error={passwordError.hasError}
						help={passwordError.hasError ? passwordError.message : ""}
						leftIcon={<Icon name="Lock24" size={16} fill={colors.neutral_80_50} />}
					/>
					<TextField.Icon
						placeholder={_T("비밀번호 확인")}
						type="password"
						autoComplete="new-password"
						onChange={(ev) => setConfirmPassword(ev.target.value)}
						leftIcon={<Icon name="Lock24" size={16} fill={colors.neutral_80_50} />}
						error={confirmError.hasError}
						help={confirmError.hasError ? confirmError.message : _T("8자~24자. 영문, 숫자, 특수문자 포함")}
					/>
				</Column>
				<Column gap={12} style={{ paddingTop: "4px", width: "100%" }}>
					<Button
						style={{ width: "100%" }}
						size="Xlarge"
						disabled={password.length == 0 || confirmPassword.length === 0}
					>
						{_T("설정하기")}
					</Button>
					<Button
						filled="secondary"
						style={{ width: "100%" }}
						size="Xlarge"
						onClick={() => modal.hide(ModalID.RESET_PASSWORD)}
					>
						{_T("취소")}
					</Button>
				</Column>
			</Column.form>
		);
	};

	useEffect(() => {
		if (accountSetting) {
			handleSave();
		}
	}, [accountSetting]);

	return (
		<Layout title="계정 설정">
			<Column gap={32} style={{ width: "100%" }}>
				<Column gap={24}>
					<Column>
						<Typography typoSize="Title1" typoColor="neutral_80_100">
							{_T("친구 요청 범위")}
						</Typography>
						<S.CheckBoxWrapper>
							<Checkbox
								id="anyoneByFriend"
								onChange={handleCheckbox}
								checked={
									(accountSetting?.allowFriendByGroupUser &&
										accountSetting?.allowFriendBySpaceUser) ||
									false
								}
							>
								<Typography typoSize="Body3" typoColor="neutral_80_100">
									{_T("누구나")}
								</Typography>
							</Checkbox>
							<Checkbox
								onChange={handleCheckbox}
								id="allowFriendByGroupUser"
								checked={accountSetting?.allowFriendByGroupUser || false}
							>
								<Typography typoSize="Body3" typoColor="neutral_80_100">
									{_T("그룹 멤버")}
								</Typography>
							</Checkbox>
							<Checkbox
								onChange={handleCheckbox}
								id="allowFriendBySpaceUser"
								checked={accountSetting?.allowFriendBySpaceUser || false}
							>
								<Typography typoSize="Body3" typoColor="neutral_80_100">
									{_T("스페이스 멤버")}
								</Typography>
							</Checkbox>
						</S.CheckBoxWrapper>
					</Column>
					<Column>
						<Typography typoSize="Title1" typoColor="neutral_80_100">
							{_T("따라오기 허용 범위")}
						</Typography>
						<S.CheckBoxWrapper>
							<Checkbox
								id="anyoneByFollow"
								checked={
									(accountSetting?.allowFollowByFriend &&
										accountSetting?.allowFollowByGroupUser &&
										accountSetting?.allowFollowBySpaceUser) ||
									false
								}
								onChange={handleCheckbox}
							>
								<Typography typoSize="Body3" typoColor="neutral_80_100">
									{_T("누구나")}
								</Typography>
							</Checkbox>
							<Checkbox
								onChange={handleCheckbox}
								id="allowFollowByGroupUser"
								checked={accountSetting?.allowFollowByGroupUser || false}
							>
								<Typography typoSize="Body3" typoColor="neutral_80_100">
									{_T("그룹 멤버")}
								</Typography>
							</Checkbox>
							<Checkbox
								onChange={handleCheckbox}
								id="allowFollowBySpaceUser"
								checked={accountSetting?.allowFollowBySpaceUser || false}
							>
								<Typography typoSize="Body3" typoColor="neutral_80_100">
									{_T("스페이스 멤버")}
								</Typography>
							</Checkbox>
							<Checkbox
								onChange={handleCheckbox}
								id="allowFollowByFriend"
								checked={accountSetting?.allowFollowByFriend || false}
							>
								<Typography typoSize="Body3" typoColor="neutral_80_100">
									{_T("친구")}
								</Typography>
							</Checkbox>
						</S.CheckBoxWrapper>
					</Column>
					<Column>
						<Typography typoSize="Title1" typoColor="neutral_80_100">
							{_T("DM 허용 범위")}
						</Typography>
						<S.CheckBoxWrapper>
							<Checkbox
								id="anyoneByDM"
								checked={
									(accountSetting?.allowDmByFriend &&
										accountSetting?.allowDmByGroupUser &&
										accountSetting?.allowDmBySpaceUser) ||
									false
								}
								onChange={handleCheckbox}
							>
								<Typography typoSize="Body3" typoColor="neutral_80_100">
									{_T("누구나")}
								</Typography>
							</Checkbox>
							<Checkbox
								onChange={handleCheckbox}
								id="allowDmByGroupUser"
								checked={accountSetting?.allowDmByGroupUser || false}
							>
								<Typography typoSize="Body3" typoColor="neutral_80_100">
									{_T("그룹 멤버")}
								</Typography>
							</Checkbox>
							<Checkbox
								onChange={handleCheckbox}
								id="allowDmBySpaceUser"
								checked={accountSetting?.allowDmBySpaceUser || false}
							>
								<Typography typoSize="Body3" typoColor="neutral_80_100">
									{_T("스페이스 멤버")}
								</Typography>
							</Checkbox>
							<Checkbox
								onChange={handleCheckbox}
								id="allowDmByFriend"
								checked={accountSetting?.allowDmByFriend || false}
							>
								<Typography typoSize="Body3" typoColor="neutral_80_100">
									{_T("친구")}
								</Typography>
							</Checkbox>
						</S.CheckBoxWrapper>
					</Column>
				</Column>
				<Column style={{ width: "100%" }}>
					<Row horizonAlign="distribute" verticalAlign="center" style={{ width: "100%" }}>
						<Typography typoSize="Title1" typoColor="neutral_80_100">
							{_T("연결된 계정")}
						</Typography>
						<Typography
							style={{ cursor: "pointer", padding: "6px 12px" }}
							typoSize="Button2_underline"
							typoColor="error_60_100"
							onClick={() => move("delete")}
						>
							{_T("계정 탈퇴")}
						</Typography>
					</Row>
					<Row horizonAlign="distribute" verticalAlign="center" style={{ width: "100%", height: "40px" }}>
						<Row gap={12} verticalAlign="center">
							<Row
								verticalAlign="center"
								horizonAlign="center"
								style={{
									border: `1px solid ${colors.neutral_50_20}`,
									borderRadius: "50%",
									width: "32px",
									height: "32px",
									padding: "6px"
								}}
							>
								<Icon name="Email24" />
							</Row>
							<Typography typoSize="Body3" typoColor="neutral_80_100">
								{_T("이메일로 연동")}
							</Typography>
						</Row>
						{connectInfo?.signupType === "EMAIL" ? (
							<Typography typoSize="Button2" typoColor="neutral_80_100" style={{ padding: "8px 12px" }}>
								{_T("가입됨")}
							</Typography>
						) : (
							<Switch
								id="email"
								checked={connectInfo?.connectedByEmail}
								onChange={(ev) => handleToggle(ev)}
							/>
						)}
					</Row>
					<Divider type="line" />
					<Row horizonAlign="distribute" verticalAlign="center" style={{ width: "100%", height: "40px" }}>
						<Row gap={12} verticalAlign="center">
							<Row
								verticalAlign="center"
								horizonAlign="center"
								style={{
									border: `1px solid ${colors.neutral_50_20}`,
									borderRadius: "50%",
									width: "32px",
									height: "32px",
									padding: "6px"
								}}
							>
								<Icon name="GoogleUnique24" />
							</Row>
							<Typography typoSize="Body3" typoColor="neutral_80_100">
								{_T("Google로 연동")}
							</Typography>
						</Row>
						{connectInfo?.signupType === "GOOGLE" ? (
							<Typography typoSize="Button2" typoColor="neutral_80_100" style={{ padding: "8px 12px" }}>
								{_T("가입됨")}
							</Typography>
						) : (
							<Switch
								id="GOOGLE"
								checked={connectInfo?.connectedIdpList?.includes("GOOGLE")}
								onChange={(ev) => handleToggle(ev)}
							/>
						)}
					</Row>
					<Divider type="line" />
					<Row horizonAlign="distribute" verticalAlign="center" style={{ width: "100%", height: "40px" }}>
						<Row gap={12} verticalAlign="center">
							<Row
								verticalAlign="center"
								horizonAlign="center"
								style={{
									border: `1px solid ${colors.neutral_50_20}`,
									borderRadius: "50%",
									width: "32px",
									height: "32px",
									padding: "6px"
								}}
							>
								<Icon name="Apple24" size={24} />
							</Row>
							<Typography typoSize="Body3" typoColor="neutral_80_100">
								{_T("Apple로 연동")}
							</Typography>
						</Row>
						{connectInfo?.signupType === "APPLE" ? (
							<Typography typoSize="Button2" typoColor="neutral_80_100" style={{ padding: "8px 12px" }}>
								{_T("가입됨")}
							</Typography>
						) : (
							<Switch
								id="APPLE"
								checked={connectInfo?.connectedIdpList?.includes("APPLE")}
								onChange={(ev) => handleToggle(ev)}
							/>
						)}
					</Row>
					<Divider type="line" />
				</Column>
				<Column>
					<Checkbox
						onChange={handleCheckbox}
						id="marketingAgreement"
						checked={accountSetting?.marketingAgreement || false}
					>
						<Typography typoColor="neutral_80_100" typoSize="Body3">
							{_T("마케팅 정보 수신 동의 (선택)")}
						</Typography>
					</Checkbox>
					<S.Help style={{ paddingLeft: "35px" }}>
						{_P(
							"마케팅 정보 수신에 동의합니다. <br />유용한 사용 팁과 화상회의 응용 방법, 멤버십 혜택, 특별한 제안을 포함한 뉴스레터를 받아 보실 수 있습니다.<br />(언제든지 철회 가능)<br />* 중요한 안내 메일은 마케팅 수신과 관련 없이 메일이 발송됩니다"
						)}
					</S.Help>
				</Column>
			</Column>
		</Layout>
	);
}
